import { useId, useState } from 'react'
import { Link } from 'react-router-dom'

import { archiveLine } from './compare.js'

// "Archived · kept whole, never deleted" — P5-3 T9, HANDOFF screen 2.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THIS SECTION IS THE PROMISE THE WHOLE FEATURE RESTS ON
// ═══════════════════════════════════════════════════════════════════════════
//
// *"Losing destinations are archived, never deleted. Every entry, star, note
// and unfurled photo survives."* And the reason, in the design's own words:
// *"next April, Portugal is three weeks of Wy's reading you don't have to do
// twice."*
//
// It is not a courtesy. Choosing is the one irreversible-feeling action on this
// screen, and the thing that makes it safe to press is that it is not
// irreversible — the service has NO delete path for a destination at all, and
// its initial migration says no slice should add one. These two buttons are
// what that decision is *for*; without them the guarantee is a comment in a
// migration nobody reads.
//
// ⚠️ TWO FACTS FROM HANDOFF'S ROW ARE MISSING AND ARE OMITTED RATHER THAN
// INVENTED — the archive DATE ("Archived 18 Aug") and the name of who did the
// collecting ("every note, photo and star **Wy** collected"). `destination`
// carries no `archived_at`, and ADR-0003 D2 means there is no name source at
// all. See compare.js's header and the PR body; both are recorded as
// found-and-not-delivered rather than quietly dropped.

export default function ArchivedPlaces({
  archived,
  tripId,
  winnerName,
  canReopen,
  onReopen,
  onCopy,
  busy = false,
}) {
  const formId = useId()
  const [copying, setCopying] = useState(null)
  const [title, setTitle] = useState('')

  const start = (column) => {
    setCopying(column.id)
    // Empty, not prefilled with the place's name. A title somebody did not type
    // is a name on their trip that they have to notice is wrong.
    setTitle('')
  }

  const submit = async (event, column) => {
    event.preventDefault()
    if (title.trim() === '') return
    const ok = await onCopy?.(column, title.trim())
    if (ok) {
      setCopying(null)
      setTitle('')
    }
  }

  return (
    <section className="d2d-archive" aria-label="Archived places" data-testid="archived">
      <p className="d2d-archive__eyebrow d2d-micro">Archived · kept whole, never deleted</p>
      <ul className="d2d-archive__list">
        {archived.map((column) => (
          <li key={column.id} className="d2d-archive__row" data-testid={`archived-${column.id}`}>
            <span className="d2d-archive__thumb" aria-hidden="true" />
            <span className="d2d-archive__text">
              <span className="d2d-archive__name">{column.name}</span>
              <span className="d2d-archive__line">{archiveLine(column, winnerName)}</span>
            </span>
            <span className="d2d-archive__actions">
              <Link className="d2d-archive__open" to={`/trips/${tripId}/places/${column.id}`}>
                Open it, read-only
              </Link>
              <button
                type="button"
                className="d2d-archive__copy"
                disabled={busy}
                data-testid={`copy-${column.id}`}
                onClick={() => start(column)}
              >
                Start next year’s trip from this
              </button>
            </span>

            {copying === column.id ? (
              <form className="d2d-archive__form" onSubmit={(event) => submit(event, column)}>
                <label className="d2d-micro" htmlFor={`${formId}-title`}>
                  What is next year’s called
                </label>
                <input
                  id={`${formId}-title`}
                  value={title}
                  required
                  maxLength={200}
                  autoFocus
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Portugal, properly this time"
                />
                {/* 🔴 Said before the button, not after it. The amounts come
                    across exactly as they were typed a year ago, which is the
                    research being copied and also a set of prices nobody has
                    checked. The stars deliberately do not come across — they
                    are somebody's opinion of a trip that does not exist yet. */}
                <p className="d2d-archive__hint">
                  Everything on it comes across — links, notes and the amounts as they were typed
                  last time. Stars do not: those are people’s, about this trip. They stay here.
                </p>
                <span className="d2d-archive__formactions">
                  <button type="submit" className="d2d-archive__go" disabled={busy}>
                    Start it
                  </button>
                  <button
                    type="button"
                    className="d2d-archive__cancel"
                    disabled={busy}
                    onClick={() => setCopying(null)}
                  >
                    Not now
                  </button>
                </span>
              </form>
            ) : null}
          </li>
        ))}
      </ul>

      <p className="d2d-archive__note">
        Every entry, star and note survives. Reopening restores the comparison exactly as it stood.
      </p>

      {canReopen ? (
        <button
          type="button"
          className="d2d-archive__reopen d2d-micro"
          disabled={busy}
          data-testid="reopen"
          onClick={() => onReopen?.()}
        >
          Reopen the comparison
        </button>
      ) : null}
    </section>
  )
}
